import { supabase } from "@/integrations/supabase/client";
import { parseVersion } from "./compareVersions";
import { getNativeAppVersion } from "./nativeAppVersion";
import { formatClientSignature, type ClientSignatureParts } from "./signature";

type ReportArgs = { p_platform: string; p_app_version: string; p_build_id: string };
export type ReportRpc = (args: ReportArgs) => PromiseLike<{ error: unknown }>;

const defaultRpc: ReportRpc = (args) => supabase.rpc("report_client_version", args);

// Déjà signalé dans ce chargement : « compte|signature ».
const reported = new Set<string>();

/**
 * Version à inscrire. Sur le web, celle de la signature (le bundle). Sur natif,
 * UNIQUEMENT la version native : la signature porte la version du bundle tant
 * que le plugin n'a pas répondu (package.json et les projets natifs n'ont pas
 * le même numéro), et l'inscrire ferait passer une app pour une autre. Null :
 * rien à signaler pour l'instant.
 */
async function resolveReportedParts(
  parts: ClientSignatureParts,
  readNativeVersion: () => Promise<string | null>,
): Promise<ClientSignatureParts | null> {
  if (parts.platform === "web") return parts;
  try {
    const nativeVersion = await readNativeVersion();
    return nativeVersion ? { ...parts, version: nativeVersion } : null;
  } catch {
    return null;
  }
}

/**
 * Inscrit la dernière version vue pour ce compte (table app_client_versions).
 * Une fois par compte et par signature dans un chargement. Tout échec est
 * avalé : ce signalement ne doit jamais gêner la personne, y compris quand le
 * front est déployé avant la migration (fonction absente).
 *
 * Sur natif, rien n'est signalé tant que la version native n'est pas lue : un
 * appel ultérieur (changement de signature, retour au premier plan) réessaie.
 */
export async function reportClientVersion(
  userId: string,
  signatureParts: ClientSignatureParts,
  rpc: ReportRpc = defaultRpc,
  readNativeVersion: () => Promise<string | null> = getNativeAppVersion,
): Promise<void> {
  const parts = await resolveReportedParts(signatureParts, readNativeVersion);
  if (!parts) return;
  if (!parseVersion(parts.version)) return;
  const key = `${userId}|${formatClientSignature(parts)}`;
  if (reported.has(key)) return;
  reported.add(key);
  try {
    const { error } = await rpc({
      p_platform: parts.platform,
      p_app_version: parts.version,
      p_build_id: parts.buildId.slice(0, 64),
    });
    if (error) reported.delete(key);
  } catch {
    reported.delete(key);
  }
}

export function resetReportedClientVersionsForTests(): void {
  reported.clear();
}
